// ============================================
// 搜索特征提取相关类型
// ============================================

/**
 * 搜索特征提取请求参数
 * 根据流程图：前端只传递 task_id，其他数据从数据库查询
 */
export interface SearchFeatureExtractRequest {
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
}

/**
 * 搜索特征提取响应数据
 */
export interface SearchFeatureExtractResponse {
    /**
     * 搜索关键字列表
     * @example ["SEARCH_FEATURE_EXTRACT:433a3dfbba19aaae3a776e8f350c64d9"]
     */
    search_keys: string[];
}

/**
 * 轮询特征拆解语义检索结果请求参数
 */
export interface SearchFeatureExtractResultRequest {
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
}

/**
 * 轮询特征拆解语义检索结果响应数据
 */
export interface SearchFeatureExtractResultResponse {
    /**
     * 任务状态
     * @example "running"
     * @description running: 进行中, done: 完成
     */
    status: string;
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
}

// ============================================
// 常见证据对比相关类型
// ============================================

/**
 * 常见证据对比请求参数
 * 根据流程图：前端传递 task_id 和 patent_ids，其他数据从数据库查询
 */
export interface CcCommonEvidenceRequest {
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
    /**
     * 专利ID列表
     * @example ["8096885f-fa60-421c-859a-a403d84cc05f", "9096885f-fa60-421c-859a-a403d84cc05f"]
     */
    patent_ids: string[];
}

/**
 * 专利公知证据数据
 * 用于存储每个专利的job_id和判断结果
 */
export interface PatentEvidenceData {
    /**
     * 任务状态
     * @example "Success"
     * @description Empty: 未初始化, Running: 运行中, Success: 成功, Error: 错误
     */
    status: CcCommonEvidenceStatus;
    /**
     * 错误信息
     * @example "任务执行失败"
     * @description 仅当status为Error时返回
     */
    error_message?: string;
    /**
     * 任务ID
     * @example "CC_COMMON_EVIDENCE:5720ef2a44f919f54bae30a21d634efa"
     */
    job_id: string;
    /**
     * 判断结果列表（轮询后填充）
     */
    judge_result?: JudgeResult[];
}

/**
 * 常见证据对比响应数据
 * 返回的是 patent_id 到 PatentEvidenceData 的映射
 */
export type CcCommonEvidenceResponse = Record<string, PatentEvidenceData>;

// ============================================
// Redis 获取常见证据结果相关类型
// ============================================

/**
 * 判断原因
 */
export interface JudgeReason {
    /**
     * 专利号
     * @example "CN109938845A"
     */
    pn: string;
    /**
     * 专利ID
     * @example "f7d502a0-a85f-449c-b369-db709030ac05"
     */
    patent_id: string;
    /**
     * 发明内容列表
     * @example ["本发明为了解决...", "为实现上述目的..."]
     */
    invention: string[];
}

/**
 * 判断结果详情
 */
export interface JudgeResult {
    /**
     * 特征编号
     * @example "F1.1.3"
     */
    feature_num: string;
    /**
     * 技术特征
     * @example "第二距离为第一距离的约1.5倍至10倍"
     */
    tech_feature: string;
    /**
     * 对比特征
     * @example "盖4高于基部部件"
     */
    comparison_feature: string;
    /**
     * 判断结果
     * @example true
     */
    judge: boolean;
    /**
     * 判断原因列表
     */
    judge_reason: JudgeReason[];
}

/**
 * 从Redis获取常见证据结果响应数据
 */
export interface PollCommonEvidenceResultResponse {
    /**
     * 任务状态
     * @example "Success"
     * @description Empty: 未初始化, Running: 运行中, Success: 成功, Error: 错误
     */
    status: CcCommonEvidenceStatus;
    /**
     * 错误信息
     * @example "任务执行失败"
     * @description 仅当status为Error时返回
     */
    error_message?: string;
    /**
     * 任务ID
     * @example "CC_COMMON_EVIDENCE:2ec3eccea4a834045d7994d72ae4ccfc"
     */
    job_id: string;
    /**
     * 判断结果列表
     */
    judge_result: JudgeResult[];
}

// ============================================
// 批量轮询相关类型
// ============================================

/**
 * 公知证据状态枚举
 * @description 用于公知证据检索任务的状态管理
 */
export type CcCommonEvidenceStatus = 'Empty' | 'Running' | 'Success' | 'Error';

/**
 * 批量轮询公知证据检索任务状态请求参数
 */
export interface PollCommonEvidenceResultBatchRequest {
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
    /**
     * 专利ID列表
     * @example ["8096885f-fa60-421c-859a-a403d84cc05f", "9096885f-fa60-421c-859a-a403d84cc05f"]
     */
    patent_ids: string[];
}

/**
 * 批量轮询公知证据检索任务状态响应数据
 * @description 包含所有专利的查询结果，以及需要继续轮询的专利ID列表
 */
export interface PollCommonEvidenceResultBatchResponse {
    /**
     * 专利ID到结果的映射
     * @description key为专利ID，value为对应的公知证据结果。只包含已经查询成功的专利结果
     */
    results: Record<string, PollCommonEvidenceResultResponse>;
    /**
     * 需要继续轮询的专利ID列表
     * @description 状态为Running或其他未完成状态的专利ID，前端需要继续轮询这些专利
     */
    pending_patent_ids: string[];
}

// ============================================
// 获取最终公知证据结果相关类型
// ============================================

/**
 * 获取公知证据最终结果请求参数
 */
export interface GetFinalCommonEvidenceResultRequest {
    /**
     * 任务ID
     * @example "task_123456"
     */
    task_id: string;
}

/**
 * 获取公知证据最终结果响应数据
 * @description 返回所有专利的完整证据数据，key为专利ID，value为证据结果
 */
export type GetFinalCommonEvidenceResultResponse = Record<string, PollCommonEvidenceResultResponse>;

// ============================================
// API 接口定义
// ============================================

/**
 * 新颖性检索PR API接口
 * @description 用于新颖性检索的所有API操作，包括特征提取和证据对比
 */
export const NoveltySearchPrApi = Symbol.for('NoveltySearchPrApi');

/**
 * 新颖性检索PR API接口
 */
export interface NoveltySearchPrApi {
    /**
     * 启动公知证据-特征提取&语义检索异步任务
     * @param data 搜索特征提取请求参数
     * @returns 搜索关键字列表
     * @description 根据任务ID启动特征拆解和语义检索异步任务
     */
    searchFeatureExtract(data: SearchFeatureExtractRequest): Promise<SearchFeatureExtractResponse>;

    /**
     * 获取公知证据-特征提取&语义检索异步任务状态
     * @param data 轮询特征拆解语义检索结果请求参数
     * @returns 任务状态
     * @description 轮询查询特征拆解和语义检索任务的执行状态
     */
    searchFeatureExtractResult(
        data: SearchFeatureExtractResultRequest,
    ): Promise<SearchFeatureExtractResultResponse>;

    /**
     * 启动公知证据检索任务
     * @param data 常见证据对比请求参数
     * @returns patent_id 到 PatentEvidenceData 的映射
     * @description 对技术特征与现有专利进行对比分析，创建公知证据检索异步任务
     */
    ccCommonEvidence(data: CcCommonEvidenceRequest): Promise<CcCommonEvidenceResponse>;

    /**
     * 批量查询公知证据检索任务状态
     * @param data 批量轮询请求参数
     * @returns patent_id 到 PollCommonEvidenceResultResponse 的映射
     * @description 批量查询多个专利的公知证据对比结果
     */
    getCommonEvidenceResultBatch(
        data: PollCommonEvidenceResultBatchRequest,
    ): Promise<PollCommonEvidenceResultBatchResponse>;

    /**
     * 获取公知证据检索最终结果
     * @param data 获取最终结果请求参数
     * @returns patent_id 到 PollCommonEvidenceResultResponse 的映射
     * @description 直接从数据库中读取完整的证据映射数据，适用于已完成的任务
     */
    getFinalCommonEvidenceResult(
        data: GetFinalCommonEvidenceResultRequest,
    ): Promise<GetFinalCommonEvidenceResultResponse>;
}
